import { cn } from 'lib/utils'
import { X } from 'lucide-react'
import { Badge } from 'primitives/badge'
import { Input } from 'primitives/input'
import { Label } from 'primitives/label'
import { type ChangeEvent, type FocusEvent, type KeyboardEvent, useState } from 'react'

interface BadgeInputProps {
  label?: string | React.ReactNode
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  endIcon?: React.ReactNode
  name?: string
  disabled?: boolean
  onValidate?: (value: string, currentValues: string[]) => string | null
  acceptKeys?: string[]
  error?: string
}

export const BadgeInput = ({
  label,
  value,
  onChange,
  placeholder = 'Use comma to separate multiple values',
  className,
  endIcon,
  name,
  disabled,
  onValidate,
  acceptKeys = [','],
  error: externalError,
}: BadgeInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputId = name || 'badge-input'
  const displayError = externalError || validationError

  const addValue = (valueToAdd: string) => {
    const trimmedValue = valueToAdd.trim()
    if (!trimmedValue) return

    // Call validation if provided
    if (onValidate) {
      const error = onValidate(trimmedValue, value)
      if (error) {
        setValidationError(error)
        return
      }
    }

    // Clear any previous validation errors
    setValidationError(null)
    onChange([...value, trimmedValue])
    setInputValue('')
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (acceptKeys.includes(e.key)) {
      e.preventDefault()
      addValue(inputValue)
    }
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    addValue(e.target.value)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError(null)
    }

    // Remove acceptKeys from input as we handle them in keyDown
    let newValue = e.target.value
    acceptKeys.forEach((key) => {
      newValue = newValue.replace(new RegExp(key, 'g'), '')
    })
    setInputValue(newValue)
  }

  const removeBadge = (index: number) => {
    const newValue = value.filter((_, i) => i !== index)
    onChange(newValue)
  }

  return (
    <div className={cn('flex flex-col gap-1', disabled && 'pointer-events-none')}>
      {label && (
        <Label htmlFor={inputId} className="mb-0.5">
          {label}
        </Label>
      )}
      <div
        className={cn(
          'flex flex-wrap gap-2 p-1 border rounded-lg bg-background focus-within:ring-2 focus-within:ring-ring min-h-[36px]',
          className,
          disabled && 'bg-bg3',
        )}
      >
        {value.map((badge, index) => (
          <Badge
            variant="default"
            key={badge}
            className={cn(
              'flex items-center gap-1 p-1 h-[24px] text-sm',
              disabled && 'opacity-50 pointer-events-none',
            )}
          >
            <span className="truncate">{badge}</span>
            {!disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label={`Remove ${badge}`}
                className="ml-1 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  removeBadge(index)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    removeBadge(index)
                  }
                }}
              >
                <X className="h-3 w-3" />
              </span>
            )}
          </Badge>
        ))}
        <Input
          id={inputId}
          type="text"
          name={name}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          variant="ghost"
          className="flex-1 min-w-[100px] h-[24px] outline-none bg-transparent placeholder:text-muted-foreground shadow-none p-0"
          placeholder={value.length === 0 ? placeholder : ''}
          endIcon={endIcon}
        />
      </div>
      {displayError && (
        <span className="text-xs-400 text-destructive mt-1 block" data-testid="badge-input-error">
          {displayError}
        </span>
      )}
    </div>
  )
}
